import { OktaAuth, EVENT_RENEWED, EVENT_ERROR } from '@okta/okta-auth-js';
import { isBefore } from 'date-fns';
import { AuthListenerEvent, LoginResult } from './entities';
import { AuthWebClientEvents, LoginResultError, LoginResultStatus } from './enums';
export class AuthWebClientOptions {
}
export class AuthWebClient {
    constructor(options) {
        this.options = options;
        if (!this.options) {
            throw new Error('AuthWebClientOptions must be provided');
        }
        if (this.options) {
            this.authClient = new OktaAuth({
                issuer: this.options.issuer,
                clientId: this.options.clientId,
                redirectUri: this.options.signInRedirect,
                scopes: ['openid', 'profile', 'patient']
            });
        }
    }
    checkTokenValidity() {
        const authToken = localStorage.getItem(this.options.authTokenName);
        const expirationToken = localStorage.getItem(this.options.expirationTokenName);
        const hasValidAuthToken = !!authToken;
        let expirationDate;
        if (expirationToken) {
            expirationDate = new Date(JSON.parse(expirationToken) * 1000);
        }
        const hasValidExpirationToken = expirationDate && isBefore(new Date(), expirationDate);
        const hasValidToken = hasValidAuthToken && hasValidExpirationToken;
        window.dispatchEvent(new AuthListenerEvent(AuthWebClientEvents.ES_PATIENT_HUB_AUTH_LISTENER, {
            detail: {
                action: 'hasValidToken',
                payload: hasValidToken,
            }
        }));
        return hasValidToken;
    }
    enableRefreshToken() {
        if (!this.authClient.options.scopes.includes('offline_access')) {
            this.authClient = new OktaAuth({
                issuer: this.options.issuer,
                clientId: this.options.clientId,
                redirectUri: this.options.signInRedirect,
                scopes: ['openid', 'profile', 'patient', 'offline_access']
            });
            this.authClient.start();
            this.authClient.tokenManager.on(EVENT_RENEWED, (key, newToken, oldToken) => {
                if (key == 'accessToken') {
                    this._setTokensInLocalStorage({ accessToken: newToken });
                }
            });
            this.authClient.tokenManager.on(EVENT_ERROR, (error) => {
                if (error?.errorCode === 'invalid_grant'
                    && error?.errorSummary?.includes('refresh token is invalid or expired')) {
                    sessionStorage.setItem('auth-error-type', 'user-deny');
                    this.logout();
                }
            });
        }
    }
    async hasActiveSession() {
        return this.authClient.isAuthenticated();
    }
    async login(username, password) {
        return new Promise((resolve) => {
            if (!this.authClient.isLoginRedirect()) {
                if (!username || !password) {
                    const result = new LoginResult();
                    result.status = LoginResultStatus.ERROR;
                    result.error = LoginResultError.INVALID_CREDENTIALS;
                    result.errorMessage = 'Username and password cannot be empty.';
                    resolve(result);
                }
                else {
                    this._attemptLoginAndRedirectToOkta(username, password, resolve);
                }
            }
            else {
                this._finishLoginAfterReturningFromOkta(resolve);
            }
        });
    }
    async logout() {
        let idToken = await this.authClient.tokenManager.get('idToken');
        localStorage.removeItem(this.options.authTokenName);
        localStorage.removeItem(this.options.expirationTokenName);
        window.dispatchEvent(new AuthListenerEvent(AuthWebClientEvents.ES_PATIENT_HUB_AUTH_LISTENER, {
            detail: {
                action: 'hasValidToken',
                payload: false,
            }
        }));
        return this.authClient.signOut({
            idToken: idToken,
            postLogoutRedirectUri: this.options.signOutRedirect,
        });
    }
    resetPassword() {
        this.authClient.token.getWithRedirect({
            state: 'resetPassword'
        }).catch((err) => {
            console.log('AuthSdkError', `${err.errorCode}: ${err.message}`);
        });
    }
    async sendForgotPasswordEmail(username) {
        return new Promise((resolve) => {
            if (!username) {
                resolve({
                    status: 'username cannot be empty'
                });
            }
            else {
                this.authClient.forgotPassword({
                    username: username,
                    factorType: 'EMAIL'
                }).then((transaction) => {
                    resolve(transaction);
                }).catch((error) => {
                    resolve(error);
                });
            }
        });
    }
    async verifyTokenAndResetPassword(password, recoveryToken) {
        return new Promise((resolve) => {
            if (!password || !recoveryToken) {
                resolve({
                    status: 'password and recoveryToken cannot be empty'
                });
            }
            else {
                this.authClient.verifyRecoveryToken({
                    recoveryToken: recoveryToken
                }).then((verifyTransaction) => {
                    verifyTransaction.resetPassword({
                        newPassword: password
                    }).then((resetPasswordTransaction) => {
                        resolve(resetPasswordTransaction);
                    }).catch((error) => {
                        resolve(error);
                    });
                }).catch((error) => {
                    resolve(error);
                });
            }
        });
    }
    _attemptLoginAndRedirectToOkta(username, password, resolve) {
        this.authClient.tokenManager.get('idToken').then(idToken => {
            const result = new LoginResult();
            if (idToken && this.checkTokenValidity()) {
                result.status = LoginResultStatus.SUCCESS;
                resolve(result);
            }
            else {
                this.authClient.signInWithCredentials({
                    username: username,
                    password: password
                }).then((transaction) => {
                    if (transaction.status === 'SUCCESS') {
                        this.authClient.token.getWithRedirect({
                            sessionToken: transaction.sessionToken
                        }).catch((err) => {
                            result.status = LoginResultStatus.ERROR;
                            result.error = LoginResultError.UNKNOWN;
                            result.errorMessage = `${err.errorCode}: ${err.message}`;
                            resolve(result);
                        });
                    }
                    else if (transaction.status === 'LOCKED_OUT') {
                        result.status = LoginResultStatus.ERROR;
                        result.error = LoginResultError.ACCOUNT_LOCKED;
                        result.errorMessage = 'Account is locked';
                        resolve(result);
                    }
                }).catch((err) => {
                    const result = new LoginResult();
                    if (err.errorCode === 'E0000004') {
                        result.error = LoginResultError.INVALID_CREDENTIALS;
                    }
                    else if (err.errorCode === 'E0000047') {
                        result.error = LoginResultError.TOO_MANY_REQUESTS;
                    }
                    else {
                        result.error = LoginResultError.UNKNOWN;
                    }
                    result.status = LoginResultStatus.ERROR;
                    result.errorMessage = `${err.errorCode}: ${err.message}`;
                    resolve(result);
                });
            }
        });
    }
    _finishLoginAfterReturningFromOkta(resolve) {
        const result = new LoginResult();
        this.authClient.token.parseFromUrl().then((tokenResponse) => {
            const tokens = tokenResponse.tokens;
            this.authClient.tokenManager.add('idToken', tokens.idToken);
            this.authClient.tokenManager.add('accessToken', tokens.accessToken);
            tokens.refreshToken && this.authClient.tokenManager.add('refreshToken', tokens.refreshToken);
            this._setTokensInLocalStorage(tokens);
            window.dispatchEvent(new AuthListenerEvent(AuthWebClientEvents.ES_PATIENT_HUB_AUTH_LISTENER, {
                detail: {
                    action: 'hasValidToken',
                    payload: true,
                }
            }));
            result.status = LoginResultStatus.SUCCESS;
            resolve(result);
        }).catch((err) => {
            result.status = LoginResultStatus.ERROR;
            result.error = LoginResultError.UNKNOWN;
            result.errorMessage = `${err.errorCode}: ${err.message}`;
            resolve(result);
        });
    }
    _setTokensInLocalStorage(tokens) {
        const accessToken = tokens.accessToken.accessToken;
        const expiresAt = (tokens.accessToken.expiresAt)?.toString();
        accessToken && localStorage.setItem(this.options.authTokenName, accessToken);
        expiresAt && localStorage.setItem(this.options.expirationTokenName, expiresAt);
    }
}
//# sourceMappingURL=auth-web-client.js.map